'use client';

import {
  type CreateHubRequest,
  type DecideHubBody,
  type DecideHubRevisionBody,
  type ListHubsAdminParams,
  type UpdateHubAddressBody,
  type UpdateHubBody
} from '@api/models';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';

import { addCalendarDays } from '@ui/lib/time';
import { retryUnlessClientError } from '@website/lib/api/apiError';
import * as hubsService from '@website/services/hubs.service';

import { adminDashboardStatsKey, useAdminDashboard } from './useAdminDashboard';

// Every key starts with 'hubs': notification pushes invalidate the whole prefix. No key is a
// prefix of another concept's: everything about one Hub sits under `hub(id)`, the lists beside it.
export const hubKeys = {
  all: ['hubs'] as const,
  /** Everything about one Hub: its detail, calendar, professionals, the viewer's standing and the Manager view. */
  hub: (id: string) => ['hubs', id] as const,
  detail: (id: string) => ['hubs', id, 'detail'] as const,
  calendar: (id: string, from: string, to: string) => ['hubs', id, 'calendar', from, to] as const,
  nextSessionOf: (id: string, professionalId: string, from: string) =>
    ['hubs', id, 'nextSessionOf', professionalId, from] as const,
  /** The public list: the Residents, as the viewer may see them. */
  residents: (id: string) => ['hubs', id, 'residents'] as const,
  /** Everyone placed there and the blocked list (Managers and admins). */
  placedProfessionals: (id: string) => ['hubs', id, 'placedProfessionals'] as const,
  /** The signed-in caller's standing at the Hub (manages it, blocked, profile status). */
  viewer: (id: string) => ['hubs', id, 'viewer'] as const,
  /** The Hub as its Managers and the admins see it. */
  managed: (id: string) => ['hubs', id, 'managed'] as const,
  /** The Hubs the caller manages. */
  mine: ['hubs', 'mine'] as const,
  search: (query: string) => ['hubs', 'search', query] as const,
  admin: (params?: ListHubsAdminParams) => ['hubs', 'admin', params ?? {}] as const,
  adminAll: ['hubs', 'admin'] as const
};

export function useHub(id: string | undefined) {
  return useQuery({
    queryKey: hubKeys.detail(id ?? ''),
    queryFn: ({ signal }) => hubsService.getPublicHub(id!, { signal }),
    enabled: !!id
  });
}

/** Seven days of a Hub's calendar from `from` (a week's Monday, or today); nothing is asked while it is null. */
export function useHubCalendar(id: string | undefined, from: string | null) {
  const to = from ? addCalendarDays(from, 6) : '';
  return useQuery({
    queryKey: hubKeys.calendar(id ?? '', from ?? '', to),
    queryFn: ({ signal }) => hubsService.getHubCalendar(id!, from!, to, { signal }),
    enabled: !!id && !!from,
    placeholderData: keepPreviousData
  });
}

/** A Resident's first session at the Hub from `from` (the week shown), never before today; nothing is asked without a Resident. */
export function useHubNextSessionOf(id: string | undefined, professionalId: string | null, from: string | null) {
  return useQuery({
    queryKey: hubKeys.nextSessionOf(id ?? '', professionalId ?? '', from ?? ''),
    queryFn: ({ signal }) => hubsService.getHubNextSessionOf(id!, professionalId!, from!, { signal }),
    enabled: !!id && !!professionalId && !!from
  });
}

/** The Hub's Residents, as the viewer may see them. */
export function useHubResidents(id: string | undefined) {
  return useQuery({
    queryKey: hubKeys.residents(id ?? ''),
    queryFn: ({ signal }) => hubsService.getHubProfessionals(id!, { signal }),
    enabled: !!id
  });
}

/** Everyone with an activity placed at the Hub, with their profile status and activities, and who is blocked (Managers and admins). */
export function useHubPlacedProfessionals(id: string | undefined) {
  return useQuery({
    queryKey: hubKeys.placedProfessionals(id ?? ''),
    queryFn: ({ signal }) => hubsService.getHubPlacedProfessionals(id!, { signal }),
    enabled: !!id
  });
}

/**
 * Whether the signed-in caller is one of the Hub's Managers and whether they may manage it (a Manager
 * or an admin), whether its Managers blocked them, and their profile's
 * review status (null: a Hub they cannot see). Enable it only for a signed-in viewer: the API answers 401 otherwise.
 */
export function useHubViewer(id: string | null | undefined, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: hubKeys.viewer(id ?? ''),
    queryFn: ({ signal }) => hubsService.getHubViewer(id!, { signal }),
    enabled: enabled && !!id,
    // Kept for the session (a sign-out reloads the page): coming back to the Hub never blinks its gear.
    gcTime: Infinity,
    retry: retryUnlessClientError
  });
}

/** How long the wizard's Hub search waits for the typing to pause. */
export const HUB_SEARCH_DEBOUNCE_MS = 250;

/**
 * The Hubs a professional can place an activity at, by name or city. An empty query lists them
 * alphabetically; one character is too few to search, so the previous results stay.
 */
export function useHubSearch(query: string, { enabled = true }: { enabled?: boolean } = {}) {
  const trimmed = query.trim();
  const [debounced] = useDebounce(trimmed, HUB_SEARCH_DEBOUNCE_MS);

  const searchable = debounced.length === 0 || debounced.length >= 2;
  const search = useQuery({
    queryKey: hubKeys.search(debounced),
    queryFn: ({ signal }) => hubsService.searchHubs(debounced, { signal }),
    enabled: enabled && searchable,
    placeholderData: keepPreviousData,
    staleTime: 60_000
  });
  const typing = trimmed !== debounced && (trimmed.length === 0 || trimmed.length >= 2);
  // A refetch of the query shown keeps its Hubs pickable; Hubs kept from another query are not.
  const loadingAnotherQuery = search.isFetching && (search.isPlaceholderData || !search.data);

  return {
    hubs: search.data?.hubs ?? [],
    isSearching: enabled && (typing || loadingAnotherQuery),
    isError: search.isError,
    refetch: search.refetch
  };
}

export function useMyHubs({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: hubKeys.mine,
    queryFn: ({ signal }) => hubsService.getMyHubs({ signal }),
    enabled
  });
}

export function useHubForManager(id: string | undefined) {
  return useQuery({
    queryKey: hubKeys.managed(id ?? ''),
    queryFn: ({ signal }) => hubsService.getHubForManager(id!, { signal }),
    enabled: !!id,
    retry: retryUnlessClientError
  });
}

export function useHubsAdmin(params?: ListHubsAdminParams) {
  return useQuery({
    queryKey: hubKeys.admin(params),
    queryFn: ({ signal }) => hubsService.listHubsAdmin(params, { signal })
  });
}

/** New Hubs and published Hubs with changes waiting for review, for the admin badges. */
export function useHubsNeedingReviewCount() {
  const { data, isLoading } = useAdminDashboard();
  return { count: data?.pendingHubs ?? 0, isLoading };
}

/** Refreshes the lists a Hub appears in: the caller's Hubs, the admin list and its counts. */
function useInvalidateHubLists() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: hubKeys.mine }),
      qc.invalidateQueries({ queryKey: hubKeys.adminAll }),
      qc.invalidateQueries({ queryKey: adminDashboardStatsKey })
    ]);
}

/** Refreshes the Hub as the public, its Managers and the admins read it, and the lists it appears in. */
function useInvalidateHub() {
  const qc = useQueryClient();
  const invalidateLists = useInvalidateHubLists();
  return (id: string) => Promise.all([qc.invalidateQueries({ queryKey: hubKeys.hub(id) }), invalidateLists()]);
}

export function useBlockHubProfessional() {
  const qc = useQueryClient();
  const invalidate = useInvalidateHub();
  return useMutation({
    mutationFn: ({ id, professionalId, reason }: { id: string; professionalId: string; reason?: string }) =>
      hubsService.blockHubProfessional(id, professionalId, reason),
    // The block releases the professional's placements: their activities no longer say "at <Hub>".
    onSuccess: (_data, { id, professionalId }) =>
      Promise.all([invalidate(id), qc.invalidateQueries({ queryKey: ['activities', 'professional', professionalId] })])
  });
}

export function useUnblockHubProfessional() {
  const invalidate = useInvalidateHub();
  return useMutation({
    mutationFn: ({ id, professionalId }: { id: string; professionalId: string }) =>
      hubsService.unblockHubProfessional(id, professionalId),
    onSuccess: (_data, { id }) => invalidate(id)
  });
}

export function useCreateHub() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateHubRequest) => hubsService.createHub(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: hubKeys.mine })
  });
}

export function useUpdateHub() {
  const invalidate = useInvalidateHub();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateHubBody }) => hubsService.updateHub(id, body),
    onSuccess: (_data, { id }) => invalidate(id)
  });
}

export function useArchiveHub() {
  const invalidate = useInvalidateHub();
  return useMutation({
    mutationFn: (id: string) => hubsService.archiveHub(id),
    onSuccess: (_data, id) => invalidate(id)
  });
}

export function useAddHubManager() {
  const invalidate = useInvalidateHub();
  return useMutation({
    mutationFn: ({ id, email }: { id: string; email: string }) => hubsService.addHubManager(id, email),
    onSuccess: (_data, { id }) => invalidate(id)
  });
}

export function useRemoveHubManager() {
  const qc = useQueryClient();
  const invalidate = useInvalidateHub();
  const invalidateLists = useInvalidateHubLists();
  return useMutation({
    /** `self`: the viewer steps down, and the caller navigates away from the Hub. */
    mutationFn: ({ id, userId }: { id: string; userId: string; self?: boolean }) =>
      hubsService.removeHubManager(id, userId),
    // A Manager who steps down leaves the Hub: its reads would answer 403 under the page they leave,
    // so they are marked stale without a refetch (they refetch when next used) and the lists refresh.
    onSuccess: (_data, { id, self = false }) =>
      self
        ? Promise.all([qc.invalidateQueries({ queryKey: hubKeys.hub(id), refetchType: 'none' }), invalidateLists()])
        : invalidate(id)
  });
}

export function useDecideHub() {
  const invalidate = useInvalidateHub();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DecideHubBody }) => hubsService.decideHub(id, body),
    onSuccess: (_data, { id }) => invalidate(id)
  });
}

export function useDecideHubRevision() {
  const invalidate = useInvalidateHub();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DecideHubRevisionBody }) => hubsService.decideHubRevision(id, body),
    onSuccess: (_data, { id }) => invalidate(id)
  });
}

export function useUpdateHubAddress() {
  const invalidate = useInvalidateHub();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateHubAddressBody }) => hubsService.updateHubAddress(id, body),
    onSuccess: (_data, { id }) => invalidate(id)
  });
}
